<template>
    <app-layout>
        <common-page-plain>
            <common-content-spacer height="medium">
                <common-page-header :heading="$t('pages.administrator.leads.referral_leakage_audit.heading')"></common-page-header>

                <div class="audit-leads">
                    <div class="audit-leads__header">
                        <div>{{ $t('pages.administrator.leads.referral_leakage_audit.table.submitted_at') }}</div>
                        <div>{{ $t('pages.administrator.leads.referral_leakage_audit.table.name') }}</div>
                        <div>{{ $t('pages.administrator.leads.referral_leakage_audit.table.email') }}</div>
                        <div>{{ $t('pages.administrator.leads.referral_leakage_audit.table.score') }}</div>
                        <div>{{ $t('pages.administrator.leads.referral_leakage_audit.table.band') }}</div>
                        <div>{{ $t('pages.administrator.leads.referral_leakage_audit.table.utm_source') }}</div>
                        <div>{{ $t('pages.administrator.leads.referral_leakage_audit.table.email_sent') }}</div>
                        <div></div>
                    </div>

                    <div v-if="isLoading" class="audit-leads__loading">
                        <common-loader :isProcessing="true"></common-loader>
                    </div>

                    <div v-else-if="!items.length" class="audit-leads__empty">
                        {{ $t('pages.administrator.leads.referral_leakage_audit.no_leads') }}
                    </div>

                    <div v-else class="audit-leads__list">
                        <div v-for="item in items" :key="item.id" class="audit-leads__row">
                            <div>{{ formatDate(item.createdAt) }}</div>
                            <div>{{ item.name }}</div>
                            <div>{{ item.email }}</div>
                            <div>{{ item.score }}/100</div>
                            <div>
                                <span :class="['badge', bandClass(item.bandKey)]">{{ item.bandLabel }}</span>
                            </div>
                            <div>{{ item.utmSource || '—' }}</div>
                            <div>{{ boolLabel(item.emailSent) }}</div>
                            <div>
                                <common-button
                                    :label="$t('common.view')"
                                    size="small"
                                    type="secondary"
                                    @click="viewDetails(item)"
                                ></common-button>
                            </div>
                        </div>
                    </div>
                </div>
            </common-content-spacer>
        </common-page-plain>

        <common-modal v-if="showDetails" @close="showDetails = false">
            <div class="details-modal">
                <h3>{{ $t('pages.administrator.leads.referral_leakage_audit.modal.title') }}</h3>
                <p><strong>{{ selected.name }}</strong> — {{ selected.email }}</p>
                <p>
                    <strong>{{ $t('pages.administrator.leads.referral_leakage_audit.table.score') }}:</strong>
                    {{ selected.score }}/100 ({{ selected.bandLabel }})
                </p>
                <p>
                    <strong>{{ $t('pages.administrator.leads.referral_leakage_audit.modal.utm') }}:</strong>
                    {{ selected.utmSource || 'direct' }}
                    <span v-if="selected.utmCampaign"> / {{ selected.utmCampaign }}</span>
                </p>
                <p>
                    <strong>{{ $t('pages.administrator.leads.referral_leakage_audit.modal.integrations') }}:</strong>
                    {{ $t('pages.administrator.leads.referral_leakage_audit.table.email_sent') }}:
                    {{ boolLabel(selected.emailSent) }}
                </p>
                <p><strong>{{ $t('pages.administrator.leads.referral_leakage_audit.modal.answers') }}:</strong></p>
                <ul class="details-modal__answers">
                    <li v-for="(points, index) in selected.answers || []" :key="index">
                        Q{{ index + 1 }}: {{ points }}/3
                    </li>
                </ul>
            </div>
        </common-modal>
    </app-layout>
</template>

<script>
    import AppLayout from 'components/layouts/AppLayout'
    import AdministratorAPI from 'api/AdministratorAPI'

    export default {
        components: { AppLayout },
        data() {
            return {
                items: [],
                isLoading: false,
                showDetails: false,
                selected: {},
            }
        },
        methods: {
            load() {
                this.isLoading = true
                AdministratorAPI.getMarketingLeads('referral-leakage-audit', { limit: 100 })
                    .then((res) => {
                        const payload = res && res.data ? res.data : res
                        this.items = payload.items || []
                    })
                    .catch(() => {
                        this.items = []
                    })
                    .finally(() => {
                        this.isLoading = false
                    })
            },
            viewDetails(item) {
                this.selected = item
                this.showDetails = true
            },
            formatDate(value) {
                if (!value) return '—'
                return new Date(value).toLocaleString()
            },
            boolLabel(value) {
                return value ? this.$t('pages.administrator.leads.yes') : this.$t('pages.administrator.leads.no')
            },
            bandClass(bandKey) {
                if (bandKey === 'tight') return 'badge--success'
                if (bandKey === 'leaking') return 'badge--warning'
                return 'badge--danger'
            },
        },
        mounted() {
            this.load()
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';

    .audit-leads__header,
    .audit-leads__row {
        display: grid;
        grid-template-columns: 1.2fr 1.2fr 1.5fr 0.7fr 1.2fr 1fr 0.7fr 0.7fr;
        gap: 1rem;
        align-items: center;
    }

    .audit-leads__header {
        padding: 1rem;
        font-weight: bold;
        background: $color--bg-dropdown;
    }

    .audit-leads__row {
        padding: 0.75rem 1rem;
        border-bottom: 1px solid $color--border-base;
    }

    .audit-leads__empty,
    .audit-leads__loading {
        padding: 2rem 1rem;
    }

    .badge {
        padding: 2px 8px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 500;
        white-space: nowrap;
    }

    .badge--success {
        background: #d4edda;
        color: #155724;
    }

    .badge--warning {
        background: #fff3cd;
        color: #856404;
    }

    .badge--danger {
        background: #f8d7da;
        color: #721c24;
    }

    .details-modal__answers {
        margin: 0;
        padding-left: 1.25rem;
    }
</style>
